import React, { useLayoutEffect, useMemo, useRef } from "react";
import { Entity } from "@common/types/entity";
import { EntitySelect } from "@components/entity/EntitySelect";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { BaseCellProps } from "./index";

interface EntityCellProps extends BaseCellProps<number> {
    entities?: Entity[];
}

export const EntityCell: React.FC<EntityCellProps> = ({
    value,
    isEditing,
    finishEditing,
    rowIdx,
    colId,
    entities = [],
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerClassName = useMemo(
        () => `entity-cell-trigger-${rowIdx}-${colId}`,
        [rowIdx, colId],
    );

    const selectedEntity = entities.find((e) => e.id === value);

    useLayoutEffect(() => {
        if (isEditing && containerRef.current) {
            const trigger = containerRef.current.querySelector(
                `.${triggerClassName}`,
            ) as HTMLElement;
            trigger?.click();
        }
    }, [isEditing, triggerClassName]);

    const handleChange = (entity?: Entity) => {
        finishEditing(entity?.id ?? null);
    };

    if (isEditing) {
        return (
            <>
                <div
                    ref={containerRef}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (["Enter", "Escape"].includes(e.key)) {
                            finishEditing();
                        }
                    }}
                >
                    <EntitySelect
                        entities={entities}
                        selectedEntity={selectedEntity}
                        onChange={handleChange}
                        onOpenChange={(open) => {
                            if (!open) {
                                finishEditing();
                            }
                        }}
                        hideArrow
                        triggerButtonProps={{
                            variant: "primary",
                            size: "sm",
                            className: triggerClassName,
                            style: {
                                position: "absolute",
                                opacity: 0,
                                pointerEvents: "none",
                            },
                        }}
                    />
                </div>
                <EntityNameDisplay entity={selectedEntity} />
            </>
        );
    }

    return <EntityNameDisplay entity={selectedEntity} />;
};

const EntityNameDisplay: React.FC<{ entity?: Entity }> = ({ entity }) => {
    if (!entity) {
        return null;
    }

    return <EntityDisplay entity={entity} />;
};
